import { Portal } from '@mantine/core';
import { IconArrowLeft } from '@tabler/icons-react';
import { Divider } from '@zorro/design-system/v2/Divider';
import { Loader } from '@zorro/design-system/v2/Loader';
import { Title } from '@zorro/design-system/v2/Title';
import {
  formatCurrencyEnUs,
  formatToMMMDayYear,
} from '@zorro/shared/formatters';
import { Center, Icon, Stack, Text } from '@zorro/web/design-system';
import {
  ReadonlyInsuredForm,
  useEnrollmentInsuredForm,
  usePremiumAmountForm,
} from '@zorro/web/shared-ui';
import { useAppRouter, useMonolithQuery } from '@zorro/web/ui-utils';

import { FormFooter } from '../../FormFooter';
import { PaymentDetails } from '../../Payments/PaymentDetails/PaymentDetails';
import { PaymentMethodViewer } from '../../Payments/PaymentDetails/paymentMethodDisplayRules';
import { ElectedPlanCard } from '../CarrierApplicationEnrollmentInfoStep/ElectedPlanCard';
import { CarrierApplicationModalStepType } from '../CarrierApplicationModal';

type Props = {
  employerId: string;
  employeeId: string;
  onboardingPeriodId: string;
  benefitId?: string;
};

export const CarrierApplicationReviewView = ({
  employerId,
  employeeId,
  onboardingPeriodId,
  benefitId,
}: Props) => {
  const { navigateToRoute } = useAppRouter();

  const { data: onboardingPeriod } = useMonolithQuery({
    method: 'onboardingPeriodsControllerFindOne',
    params: [employeeId, onboardingPeriodId],
  });

  const insuredForm = useEnrollmentInsuredForm({
    onboardingPeriodId,
    employeeId,
    employerId,
    isFinalizationMode: false,
    isEmployeeMode: false,
    shouldShowLoadingOverlay: false,
  });

  const { premiumAmountForm } = usePremiumAmountForm({
    mode: 'fetch',
    employeeId,
    onboardingPeriodId,
    benefitId,
    shouldShowLoadingOverlay: false,
  });
  const premium = premiumAmountForm.watch('premium');

  const handleBackClick = () => {
    navigateToRoute(
      'OPS_EMPLOYEE_PLANS_TAB',
      [{ employerId, employeeId, onboardingPeriodId }],
      {
        step: CarrierApplicationModalStepType.COMPLETE_APPLICATION,
        ...(benefitId && { benefitId }),
      },
      false,
      false
    );
  };

  if (!onboardingPeriod) {
    return (
      <Center mih="calc(100vh - 18.5rem)">
        <Loader />
      </Center>
    );
  }

  return (
    <>
      <Stack mih="calc(100vh - 18.5rem)" pl="xl" pb="xxl" gap={0}>
        <Stack pt="md">
          <Title order={1}>Review application details</Title>
          <Divider />
        </Stack>
        <Stack pt="xl" gap="xl">
          <Stack gap="md">
            <Text fw={600}>Elected plan</Text>
            <ElectedPlanCard
              onboardingPeriodId={onboardingPeriodId}
              benefitId={benefitId}
            />
          </Stack>

          <Stack gap="md">
            <Text fw={600}>Who is covered</Text>
            <ReadonlyInsuredForm
              insuredForm={insuredForm}
              onboardingPeriodId={onboardingPeriodId}
              withAgeAtPlanStart
              accordionVariant="shadow"
            />
          </Stack>

          <Stack gap="md">
            <Text fw={600}>Premium amount</Text>
            <Text>
              {formatCurrencyEnUs(premium, { minimumFractionDigits: 2 })}
            </Text>
          </Stack>

          <Stack gap="md">
            <Text fw={600}>Requested coverage period</Text>
            <Text>
              {`${formatToMMMDayYear(onboardingPeriod.effectiveFrom)} – ${formatToMMMDayYear(onboardingPeriod.effectiveUntil)}`}
            </Text>
          </Stack>

          <Stack gap="md">
            <Text fw={600}>Payment method</Text>
            <PaymentDetails
              viewer={PaymentMethodViewer.OPS}
              employeeId={employeeId}
              employerId={employerId}
              onboardingPeriodId={onboardingPeriodId}
            />
          </Stack>
        </Stack>
      </Stack>
      <Portal target="#application-modal-footer">
        <FormFooter
          tertiaryLabel="Back to complete application"
          tertiaryButtonProps={{
            onClick: handleBackClick,
            mr: 'auto',
            leftSection: <Icon icon={IconArrowLeft} />,
          }}
          paperProps={{ pos: 'sticky', bottom: '0' }}
        />
      </Portal>
    </>
  );
};
